/**
 * /policies/eos/termination-reasons — Session 3c PR 26.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { TerminationReasonItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function EosTerminationReasonsPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-eos-termination-reasons", le],
    queryFn: () => session3cApi.listTerminationReasons(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<TerminationReasonItem>[] = [
    {
      headerKey: "policies.eos_termination_reasons.col.code",
      label: "Reason code",
      render: (r) => <span className="font-medium">{r.reason_code}</span>,
    },
    {
      headerKey: "policies.eos_termination_reasons.col.article",
      label: "Article",
      render: (r) => <span className="font-mono text-xs">{r.article_reference}</span>,
    },
    {
      headerKey: "policies.eos_termination_reasons.col.eos",
      label: "Affects EOS",
      render: (r) => <span className="text-xs">{r.affects_eos ? "yes" : "—"}</span>,
    },
    {
      headerKey: "policies.eos_termination_reasons.col.notice",
      label: "Affects notice",
      render: (r) => <span className="text-xs">{r.affects_notice_period ? "yes" : "—"}</span>,
    },
    {
      headerKey: "policies.eos_termination_reasons.col.absconding",
      label: "Art. 80 absconding",
      render: (r) => <span className={`text-xs ${r.article_80_absconding ? "font-semibold text-purple-800" : "text-text-tertiary"}`}>{r.article_80_absconding ? "yes (5 pre-conds)" : "—"}</span>,
    },
  ];

  return (
    <PolicyListPage<TerminationReasonItem>
      title={t("policies.eos_termination_reasons.title", "Termination Reason Codes")}
      subtitle={t("policies.eos_termination_reasons.subtitle", "11-subtype Article 80 + with/without/mutual cause codes. Mutaba'aa enforces Article 80 absconding pre-conditions at commit.")}
      agent="mutabaa"
      domain="eos_termination_reasons"
      newButtonLabel={t("policies.eos_termination_reasons.cta.new", "New Reason Code")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.eos_termination_reasons.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.eos_termination_reasons.suggestion.clean_body", "Article 80 absconding subtype requires ALL 5 pre-conditions at termination commit (enforced by M18 + M30 P6)."),
          primaryActionLabel: t("policies.eos_termination_reasons.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "eos-termination-reasons-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.eos_termination_reasons.title", "Termination Reason Codes"),
        window_label: t("policies.eos_termination_reasons.window", "active codes"),
      }}
    />
  );
}
